/**
 * API Service Layer
 *
 * Handles all communication with the PHP backend API
 */

import type {
  GridConfiguration,
  JobFunctionGroup,
  JobFunction,
  JobLevel,
  CareerTrack,
  JobCard,
  NewJobFunctionGroup,
  NewJobFunction,
  NewJobLevel,
  NewCareerTrack,
  NewJobCard,
  UpdateJobCard,
  UpdateCardPosition,
  ReorderCards,
  ApiResponse,
  ApiError,
} from '../types/api';

// Get API base URL from environment or default to current origin
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;
const API_PREFIX = '/api/v1';

/**
 * Custom error class for API errors
 */
export class ApiException extends Error {
  statusCode: number;
  errors?: Record<string, string[]>;

  constructor(
    message: string,
    statusCode: number,
    errors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiException';
    this.statusCode = statusCode;
    this.errors = errors;
  }
}

/**
 * Base fetch wrapper with error handling
 */
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${API_PREFIX}${endpoint}`;
  const method = options.method || 'GET';
  const body = options.body ? JSON.parse(options.body as string) : undefined;

  console.log('[API Call]', method, endpoint, body || '');

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  // Handle 204 No Content
  if (response.status === 204) {
    console.log('[API Response]', method, endpoint, '204 No Content');
    return undefined as T;
  }

  const data = await response.json();

  if (!response.ok) {
    console.error('[API Response]', method, endpoint, response.status, data);
    const error = data as ApiError;
    throw new ApiException(
      error.message || 'An error occurred',
      response.status,
      error.errors
    );
  }

  console.log('[API Response]', method, endpoint, data);
  return data;
}

/**
 * Grid Configuration API
 */
export const gridApi = {
  /**
   * Get complete grid configuration
   */
  async getConfiguration(): Promise<ApiResponse<GridConfiguration>> {
    return fetchApi<ApiResponse<GridConfiguration>>('/grid');
  },

  // Job Function Groups
  async createJobFunctionGroup(group: NewJobFunctionGroup): Promise<ApiResponse<JobFunctionGroup>> {
    return fetchApi<ApiResponse<JobFunctionGroup>>('/grid/job-function-groups', {
      method: 'POST',
      body: JSON.stringify(group),
    });
  },

  async updateJobFunctionGroup(id: number, updates: Partial<NewJobFunctionGroup>): Promise<ApiResponse<JobFunctionGroup>> {
    return fetchApi<ApiResponse<JobFunctionGroup>>(`/grid/job-function-groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  async deleteJobFunctionGroup(id: number): Promise<void> {
    return fetchApi<void>(`/grid/job-function-groups/${id}`, {
      method: 'DELETE',
    });
  },

  // Job Functions
  async createJobFunction(func: NewJobFunction): Promise<ApiResponse<JobFunction>> {
    return fetchApi<ApiResponse<JobFunction>>('/grid/job-functions', {
      method: 'POST',
      body: JSON.stringify(func),
    });
  },

  async updateJobFunctions(functions: JobFunction[]): Promise<ApiResponse<JobFunction[]>> {
    return fetchApi<ApiResponse<JobFunction[]>>('/grid/job-functions', {
      method: 'PATCH',
      body: JSON.stringify(functions),
    });
  },

  async deleteJobFunction(id: number): Promise<void> {
    return fetchApi<void>(`/grid/job-functions/${id}`, {
      method: 'DELETE',
    });
  },

  // Job Levels
  async createJobLevel(level: NewJobLevel): Promise<ApiResponse<JobLevel>> {
    return fetchApi<ApiResponse<JobLevel>>('/grid/job-levels', {
      method: 'POST',
      body: JSON.stringify(level),
    });
  },

  async updateJobLevels(levels: JobLevel[]): Promise<ApiResponse<JobLevel[]>> {
    return fetchApi<ApiResponse<JobLevel[]>>('/grid/job-levels', {
      method: 'PATCH',
      body: JSON.stringify(levels),
    });
  },

  async deleteJobLevel(id: number): Promise<void> {
    return fetchApi<void>(`/grid/job-levels/${id}`, {
      method: 'DELETE',
    });
  },

  // Career Tracks
  async createCareerTrack(track: NewCareerTrack): Promise<ApiResponse<CareerTrack>> {
    return fetchApi<ApiResponse<CareerTrack>>('/grid/career-tracks', {
      method: 'POST',
      body: JSON.stringify(track),
    });
  },

  async deleteCareerTrack(id: number): Promise<void> {
    return fetchApi<void>(`/grid/career-tracks/${id}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Job Cards API
 */
export const cardsApi = {
  /**
   * Get all job cards (optionally filtered)
   */
  async getAll(filters?: {
    function_id?: number;
    level_id?: number;
  }): Promise<ApiResponse<JobCard[]>> {
    const params = new URLSearchParams();
    if (filters?.function_id) params.set('function_id', filters.function_id.toString());
    if (filters?.level_id) params.set('level_id', filters.level_id.toString());

    const query = params.toString() ? `?${params.toString()}` : '';
    return fetchApi<ApiResponse<JobCard[]>>(`/cards${query}`);
  },

  /**
   * Get a single job card by ID
   */
  async getById(id: number): Promise<ApiResponse<JobCard>> {
    return fetchApi<ApiResponse<JobCard>>(`/cards/${id}`);
  },

  /**
   * Create a new job card
   */
  async create(card: NewJobCard): Promise<ApiResponse<JobCard>> {
    return fetchApi<ApiResponse<JobCard>>('/cards', {
      method: 'POST',
      body: JSON.stringify(card),
    });
  },

  /**
   * Update a job card (full update)
   */
  async update(id: number, card: UpdateJobCard): Promise<ApiResponse<JobCard>> {
    return fetchApi<ApiResponse<JobCard>>(`/cards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(card),
    });
  },

  /**
   * Partial update a job card
   */
  async partialUpdate(id: number, updates: Partial<UpdateJobCard>): Promise<ApiResponse<JobCard>> {
    return fetchApi<ApiResponse<JobCard>>(`/cards/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Move a card to a new position (different cell)
   */
  async updatePosition(
    id: number,
    position: UpdateCardPosition
  ): Promise<ApiResponse<JobCard>> {
    return fetchApi<ApiResponse<JobCard>>(`/cards/${id}/position`, {
      method: 'PATCH',
      body: JSON.stringify(position),
    });
  },

  /**
   * Reorder cards within the same cell
   */
  async reorder(request: ReorderCards): Promise<ApiResponse<{ updated: number }>> {
    return fetchApi<ApiResponse<{ updated: number }>>('/cards/reorder', {
      method: 'PATCH',
      body: JSON.stringify(request),
    });
  },

  /**
   * Delete a job card
   */
  async delete(id: number): Promise<void> {
    return fetchApi<void>(`/cards/${id}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Helper to parse JSON fields from API responses
 */
export function parseJsonField<T>(field: string | null): T[] {
  if (!field) return [];
  try {
    return JSON.parse(field);
  } catch {
    return [];
  }
}
